"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { AlertTriangle, Briefcase, Eye, EyeOff, GraduationCap, KeyRound, Loader2, Lock, Mail } from "lucide-react";
import { useToast } from "@/components/portal/Toast";
import { apiLogin, ApiError, LOGIN_NOTICE_KEY } from "@/lib/portal/api";
import { readRemembered, readRememberedPasswords, readSession, writeRemembered, writeSession } from "@/lib/portal/session";
import { cn } from "@/lib/utils";

type Role = "student" | "employee";

const roles: Record<Role, { label: string; icon: typeof GraduationCap; idLabel: string; idHint: string; blurb: string }> = {
  student: {
    label: "Student",
    icon: GraduationCap,
    idLabel: "Email or username",
    idHint: "you@example.com",
    blurb: "Access your courses, attendance, fees and certificates.",
  },
  employee: {
    label: "Academy",
    icon: Briefcase,
    idLabel: "Work email or username",
    idHint: "name@myvalueacademy.in",
    blurb: "Access staff tools, batches, leads and learner records.",
  },
};

export function LoginForm({ initialRole = "student", next }: { initialRole?: Role; next?: string }) {
  const [role, setRole] = useState<Role>(initialRole);
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<{ identifier?: string; password?: string }>({});
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [remember, setRemember] = useState(false);
  const [formError, setFormError] = useState("");
  const router = useRouter();
  const toast = useToast();
  const r = roles[role];

  // Already signed in: there is nothing to do here, so go straight to the dashboard.
  useEffect(() => {
    if (readSession()) router.replace(next ?? "/dashboard");
  }, [router, next]);

  // Pre-fill the remembered login name (localStorage is only readable on the client)
  useEffect(() => {
    const saved = readRemembered()[initialRole];
    if (saved) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one-time sync of localStorage into React state
      setIdentifier(saved);
      setPassword(readRememberedPasswords()[initialRole] ?? "");
      setRemember(true);
    }
  }, [initialRole]);

  // A session that was ended behind the user's back (e.g. the account was deactivated) explains itself here.
  useEffect(() => {
    try {
      const notice = window.sessionStorage.getItem(LOGIN_NOTICE_KEY);
      if (notice) {
        window.sessionStorage.removeItem(LOGIN_NOTICE_KEY);
        // eslint-disable-next-line react-hooks/set-state-in-effect -- one-time read of a message left by the previous page
        setFormError(notice);
      }
    } catch {
      /* storage blocked */
    }
  }, []);

  function validate(): boolean {
    const errs: typeof errors = {};
    if (!identifier.trim()) errs.identifier = "Enter your email or username.";
    if (!password) errs.password = "Enter your password.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (busy || !validate()) return;
    setFormError("");
    setBusy(true);
    try {
      const { session, firstLogin } = await apiLogin({
        identifier: identifier.trim(),
        password,
        role,
        rememberMe: remember,
      });
      writeSession(session);
      writeRemembered(session.role, remember ? identifier.trim() : null, remember ? password : null);
      toast.success(`Welcome back, ${session.name.split(" ")[0]}!`, `Signed in as ${session.role}.`);
      const safeNext = next && next.startsWith("/dashboard") ? next : "/dashboard";
      // first sign-in of an auto-created account: offer the (skippable) change-password screen
      router.push(firstLogin ? "/change-password?first=1" : safeNext);
    } catch (err) {
      setBusy(false);
      const message = err instanceof ApiError ? err.message : "Login failed. Please try again.";
      setFormError(message);
      toast.error("Sign in failed", message);
    }
  }

  return (
    <div className="rounded-[var(--radius-card)] border border-line bg-white p-6 shadow-lift sm:p-8">
      <h1 className="font-display text-3xl font-semibold text-ink">Welcome back</h1>
      <p className="mt-1 text-sm text-ink-soft">{r.blurb}</p>

      <div className="mt-6 grid grid-cols-2 gap-1 rounded-xl bg-cream-deep p-1" role="tablist" aria-label="Login type">
        {(Object.keys(roles) as Role[]).map((k) => {
          const Icon = roles[k].icon;
          return (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={role === k}
              onClick={() => {
                setRole(k);
                setErrors({});
                const saved = readRemembered()[k];
                if (saved) {
                  setIdentifier(saved);
                  setPassword(readRememberedPasswords()[k] ?? "");
                  setRemember(true);
                } else {
                  setPassword("");
                  setRemember(false);
                }
              }}
              className={cn(
                "inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold transition-all",
                role === k ? "bg-white text-brand-700 shadow-soft" : "text-ink-soft hover:text-ink"
              )}
            >
              <Icon className="h-4 w-4" /> {roles[k].label}
            </button>
          );
        })}
      </div>

      {formError && (
        <div role="alert" className="mt-5 flex items-start gap-2.5 rounded-xl border border-[#b3261e]/30 bg-[#b3261e]/5 px-3.5 py-3 text-sm text-[#b3261e]">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{formError}</span>
        </div>
      )}

      <form className="mt-6 space-y-4" onSubmit={onSubmit} noValidate>
        <div>
          <label htmlFor="login-id" className="mb-1.5 block text-sm font-medium text-ink">{r.idLabel}</label>
          <div className="relative">
            <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
            <input
              id="login-id"
              name="id"
              autoComplete="username"
              placeholder={r.idHint}
              value={identifier}
              onChange={(e) => { setIdentifier(e.target.value); if (formError) setFormError(""); }}
              aria-invalid={!!errors.identifier}
              aria-describedby={errors.identifier ? "login-id-error" : undefined}
              className={cn(
                "h-11 w-full rounded-[var(--radius-btn)] border bg-white pl-10 pr-3 text-[15px] outline-none transition-colors focus:border-brand-500",
                errors.identifier ? "border-[#b3261e]" : "border-line"
              )}
            />
          </div>
          {errors.identifier && (
            <p id="login-id-error" role="alert" className="mt-1.5 text-[13px] font-medium text-[#b3261e]">
              {errors.identifier}
            </p>
          )}
        </div>
        <div>
          <label htmlFor="login-pass" className="mb-1.5 block text-sm font-medium text-ink">Password</label>
          <div className="relative">
            <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
            <input
              id="login-pass"
              name="password"
              type={show ? "text" : "password"}
              autoComplete="current-password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => { setPassword(e.target.value); if (formError) setFormError(""); }}
              aria-invalid={!!errors.password}
              aria-describedby={errors.password ? "login-pass-error" : undefined}
              className={cn(
                "h-11 w-full rounded-[var(--radius-btn)] border bg-white pl-10 pr-11 text-[15px] outline-none transition-colors focus:border-brand-500",
                errors.password ? "border-[#b3261e]" : "border-line"
              )}
            />
            <button
              type="button"
              onClick={() => setShow((v) => !v)}
              aria-label={show ? "Hide password" : "Show password"}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-ink-muted hover:text-ink"
            >
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {errors.password && (
            <p id="login-pass-error" role="alert" className="mt-1.5 text-[13px] font-medium text-[#b3261e]">
              {errors.password}
            </p>
          )}
        </div>
        <div className="flex items-center justify-between text-sm">
          <label className="inline-flex items-center gap-2 text-ink-soft">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="h-4 w-4 accent-brand-600" /> Remember me
          </label>
          <Link href="/forgot-password" className="font-medium text-brand-700 hover:underline">
            Forgot password?
          </Link>
        </div>
        <button
          type="submit"
          disabled={busy}
          className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-[var(--radius-btn)] bg-brand-600 text-base font-medium text-white shadow-soft transition-all hover:bg-brand-700 disabled:opacity-70"
        >
          {busy ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Signing in…
            </>
          ) : (
            <>
              <KeyRound className="h-4 w-4" /> Login as {r.label}
            </>
          )}
        </button>
      </form>

      <p className="mt-6 text-center text-sm text-ink-soft">
        {role === "student" ? (
          <>
            New student? <Link href="/contact?tab=enquiry" className="font-semibold text-brand-700 hover:underline">Send an enquiry</Link>
          </>
        ) : (
          <>
            New here? <Link href="/contact?tab=enquiry" className="font-semibold text-brand-700 hover:underline">Send an enquiry</Link>
          </>
        )}
      </p>
    </div>
  );
}
